"use client";
import type { IResource } from "@gettactic/api";
import { InteractiveZone } from "@gettactic/helpers/src/map/types";
import type { IResourceWithSchedules } from "@gettactic/hooks";
import React from "react";
import { createPortal } from "react-dom";
import { usePopper } from "react-popper";
import { MapPopoverShell } from "@/features/map/components/MapPopoverShell";
import { ResourceHoverPreview } from "@/features/map/components/ResourceHoverPreview";
import { useMap } from "@/features/map/MapProvider";

type MapViewHoverDetailsProps = {
  resource: IResourceWithSchedules | null;
  setResource: React.Dispatch<React.SetStateAction<IResource | null>>;
  zones: InteractiveZone[];
  floor: { name: string } | null;
  openReservation: (res: IResource) => void;
  setPopperElement: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
  formattedDate: string;
  enablePortal?: boolean;
  highlightUserId?: string | undefined;
  mapContainerRef: React.RefObject<HTMLDivElement | null>;
};

export function MapViewHoverDetails({
  resource,
  enablePortal = false,
  mapContainerRef
}: MapViewHoverDetailsProps) {
  // Popper styling instructions for resource details hover

  const { events, startDate } = useMap();
  const [popperElement, setPopperElement] = React.useState<HTMLElement | null>(
    null
  );

  const [hoveringElement, setHoveringElement] =
    React.useState<HTMLElement | null>(null);

  React.useEffect(() => {
    const highlightResource = (resource: IResource | null) => {
      if (!resource) {
        setHoveringElement(null);

        return;
      }
      const hoveringElement = (
        mapContainerRef.current || document
      ).querySelector<HTMLDivElement>(`#interactive-${resource.id}`);
      setHoveringElement(hoveringElement);
    };
    events.register("onHighlightResource", highlightResource);

    return () => events.unregister("onHighlightResource", highlightResource);
  }, [!!mapContainerRef.current]);

  const {
    styles: stylesDetailsHighlight,
    attributes: attributesDetailsHighlight
  } = usePopper(hoveringElement, popperElement, {
    placement: "auto",
    strategy: "fixed",
    modifiers: [
      {
        name: "offset",
        enabled: true,
        options: {
          offset: [10, 10]
        }
      }
    ]
  });

  if (!resource) {
    return null;
  }

  const content = (
    <MapPopoverShell
      ref={setPopperElement}
      style={stylesDetailsHighlight.popper}
      {...attributesDetailsHighlight}
      // pointer-events-none: the card is purely informational. Without it,
      // when it opens under the cursor (large area-only rooms anchor near
      // the hidden dot), the pointer "enters" the card, the spot fires
      // mouseleave, the card unmounts, and every subsequent mouse move
      // re-opens/closes it — reading as "the popover never comes back".
      className="pointer-events-none absolute z-popover hidden max-w-xs flex-1 rounded-[14px] border-0 md:block"
    >
      <ResourceHoverPreview resource={resource} now={startDate ?? new Date()} />
    </MapPopoverShell>
  );

  if (enablePortal) {
    return createPortal(content, document.body);
  }

  return content;
}
